import { CircleDollarIcon, CouponIcon } from "@xola/icons";
import { almostZero, Button, Tooltip } from "@xola/ui-kit";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import clsx from "clsx";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { useHistory } from "react-router-dom";
import {
    AffiliateStatus,
    LineItemType,
    Partner,
    PartnerObject,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseStatus,
    XolaPartnerStatus,
} from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { Dues, getPartnerCommissionsReport, PartnersReportData } from "../../../reports/PartnerCommissionsReport";
import {
    AffiliatePaymentStatus,
    usePartnerCommissionsFilterStore,
} from "../../../stores/reports/partnerCommissionsFilterStore";
import { Currency } from "../../Common/Currency";
import { TableSkeleton } from "../../Common/Skeleton/TableSkeleton";
import { ColumnLayout } from "../../Layout/ColumnLayout";
import { PartnerCommissionsTable } from "./PartnerCommissionsTable";
import { PaymentSettlementData, PaymentSettlementModal, SettlementType } from "./PaymentSettlementModal";
import { getAgentTermValue } from "@/components/Agent/AgentTable";

interface PartnerCommissionsListProps {
    partners: Partner[];
    termsTemplates: PurchaseRuleLink[];
    purchaseRules: PurchaseRule[];
}

export const PartnerCommissionsList = ({ partners, termsTemplates, purchaseRules }: PartnerCommissionsListProps) => {
    const seller = useSeller();
    const [from, to] = usePartnerCommissionsFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = usePartnerCommissionsFilterStore((state) => state.filters);

    const [paymentSettlementData, setPaymentSettlementData] = useState<PaymentSettlementData>();

    const params = { sellerId: seller.id, filters, from, to, status: PurchaseStatus.COMMITTED };
    const { data: reportData, isLoading } = useQuery(
        ["partnerCommissionsReport", params],
        async () => getPartnerCommissionsReport(params),
        { staleTime: 0 },
    );

    const handleSettlementButtonClick = (partnersReportData: PartnersReportData) => {
        const paymentSettlementType = partnersReportData.balanceDue > 0 ? SettlementType.PAY : SettlementType.COLLECT;
        setPaymentSettlementData({
            partnerId: partnersReportData.id,
            balanceDue: partnersReportData.balanceDue,
            paymentSettlementType: paymentSettlementType,
            children: !!partnersReportData.children?.length,
        });
    };

    if (isLoading || !reportData) {
        return <TableSkeleton rows={7} columns={5} />;
    }

    // Add other needed data to partners array
    let partnersReportData: PartnersReportData[] = generatePartnerReportData(
        partners,
        seller.currency,
        termsTemplates,
        purchaseRules,
        reportData,
    );

    // Sum all children partner balance due and add to Parent partner
    partnersReportData = addChildrenBalanceDueToPartner(partnersReportData);
    partnersReportData = partnersReportData.filter(
        (partner) => partner.status === AffiliateStatus.ACTIVE || partner.status === XolaPartnerStatus.ACCEPTED,
    );

    const filteredPartners =
        filters.affiliatePaymentStatus === AffiliatePaymentStatus.PENDING
            ? partnersReportData.filter((partner) => !almostZero(partner.balanceDue))
            : partnersReportData;

    return (
        <>
            <ColumnLayout.Fill>
                <div className="w-full py-4 px-6">
                    {filteredPartners?.length > 0 ? (
                        <PartnerCommissionsTable
                            columnDefs={getColumnDefs(handleSettlementButtonClick)}
                            values={filteredPartners}
                        />
                    ) : (
                        <span className="flex items-center justify-center pt-25">No Partners found.</span>
                    )}
                </div>
            </ColumnLayout.Fill>

            <PaymentSettlementModal
                paymentSettlementData={paymentSettlementData}
                isOpen={!!paymentSettlementData}
                onClose={() => setPaymentSettlementData(undefined)}
            />
        </>
    );
};

const generatePartnerReportData = (
    partners: Partner[],
    currency: string,
    termsTemplates: PurchaseRuleLink[],
    purchaseRules: PurchaseRule[],
    reportData: Dues,
) => {
    return partners.map((partner: Partner) => {
        const partnerId = partner.id;
        const partnerCommissionTerm = getAgentTermValue(
            partnerId,
            currency,
            LineItemType.AFFILIATE_COMMISSION,
            termsTemplates,
            purchaseRules,
            partner.object,
        );
        const partnerDiscountTerm = getAgentTermValue(
            partnerId,
            currency,
            LineItemType.AFFILIATE_DISCOUNT,
            termsTemplates,
            purchaseRules,
            partner.object,
        );

        const balanceDue = getPartnerDue(partnerId, reportData);

        return {
            ...partner,
            ...(partner.partnerSeller && { name: partner.partnerSeller.name }),
            balanceDue,
            partnerCommissionTerm,
            partnerDiscountTerm,
        };
    });
};

const addChildrenBalanceDueToPartner = (partnersReportData: PartnersReportData[]) => {
    return partnersReportData.map((partner) => {
        if (partner.children) {
            let childrenBalanceDue = 0;
            for (const childrenId of partner.children) {
                const childrenData = partnersReportData.find((item) => item.id === childrenId);
                childrenBalanceDue += childrenData?.balanceDue ?? 0;
            }

            return {
                ...partner,
                balanceDue: partner.balanceDue + childrenBalanceDue,
            };
        }

        return partner;
    });
};

export const getPartnerDue = (partnerId: ID, reportData: Dues) => {
    const aggregatedPartnerData = reportData[partnerId] || {};
    const commission = aggregatedPartnerData.commission ?? 0;
    const deposit = aggregatedPartnerData.deposit ?? 0;
    const payment = aggregatedPartnerData.payment ?? 0;
    return commission - deposit - payment;
};

const getColumnDefs = (handleSettlementButtonClick: (data: PartnersReportData) => void) => {
    const columnDefs: ColDef[] = [
        {
            field: "name",
            headerName: "Partner",
            cellRenderer: (props: ICellRendererParams) => <ClickableCell props={props} />,
            cellClass: "no-translate",
        },
        {
            headerName: "Partner Type",
            cellRenderer: (props: ICellRendererParams) => {
                const { data }: { data: PartnersReportData } = props;
                if (!data) {
                    return null;
                }

                const partnerType = data.object === PartnerObject.XOLA_PARTNER ? "Operator" : "Agent";

                return <div>{partnerType}</div>;
            },
            cellClass: "no-translate",
        },
        {
            field: "code",
            headerName: "Code",
            cellRenderer: (props: ICellRendererParams) => <ClickableCell props={props} />,
            cellClass: "no-translate",
        },
        {
            headerName: "Terms",
            maxWidth: 100,
            cellRenderer: (props: ICellRendererParams) => {
                const { data }: { data: PartnersReportData } = props;
                if (!data) {
                    return null;
                }

                const partnerCommissionTerm = data.partnerCommissionTerm
                    ? `${data.partnerCommissionTerm} Commission`
                    : "No Commission";
                const partnerDiscountTerm = data.partnerDiscountTerm
                    ? `${data.partnerDiscountTerm} Discount`
                    : "No Discount";

                return (
                    <div className="space-x-2">
                        <Tooltip content={partnerCommissionTerm}>
                            <CircleDollarIcon className={clsx(!data.partnerCommissionTerm && "opacity-50")} />
                        </Tooltip>
                        <Tooltip content={partnerDiscountTerm}>
                            <CouponIcon className={clsx(!data.partnerDiscountTerm && "opacity-50")} />
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: "balanceDue",
            headerName: "Balance Due",
            headerClass: "text-right",
            cellClass: "text-right tabular-nums",
            maxWidth: 120,
            cellRenderer: (props: ICellRendererParams) => {
                const balanceDue = props.value;
                if (!props.value || almostZero(balanceDue)) {
                    return <span>--</span>;
                }

                return <Currency>{balanceDue}</Currency>;
            },
        },
        {
            headerName: "Take Action",
            cellRenderer: (props: ICellRendererParams) => {
                const { data }: { data: PartnersReportData } = props;
                if (!data || almostZero(data.balanceDue)) {
                    return <span>--</span>;
                }

                return (
                    <Button
                        color={data.balanceDue > 0 ? "success" : "primary"}
                        size="small"
                        onClick={() => handleSettlementButtonClick(data)}
                    >
                        {data.balanceDue > 0 ? "Pay" : "Collect"}
                    </Button>
                );
            },
        },
    ];

    return columnDefs;
};

const ClickableCell = ({ props }: { props: ICellRendererParams }) => {
    const history = useHistory();

    const handleCellClick = () => {
        history.push(`/reports/partnerStatement/${props.data.id}`);
    };

    return (
        <div className="cursor-pointer" onClick={handleCellClick}>
            {props.value}
        </div>
    );
};
